import { useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import QRCode from 'qrcode';
import styles from './ShareModal.module.css';

export default function ShareModal({
  modalOpen,
  onHide,
  assistantName,
  assistantToken,
  onCopySuccess,
  onCopyFail,
}) {
  const inputRef = useRef(null);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [qrLoading, setQrLoading] = useState(false);

  const shareUrl = useMemo(() => {
    const token = assistantToken || '';
    if (!token) return '';
    const path = `/chat/${token}`;
    if (typeof window === 'undefined') return path;
    const origin = window.location.origin || '';
    return origin ? `${origin}${path}` : path;
  }, [assistantToken]);

  const safeFileName = useMemo(() => {
    const base = (assistantName || 'assistant')
      .replace(/[\\/:*?"<>|]+/g, '_')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 40);
    return `${base || 'assistant'}-qrcode.png`;
  }, [assistantName]);

  useEffect(() => {
    if (!modalOpen || !shareUrl) {
      setQrDataUrl('');
      setQrLoading(false);
      return;
    }

    let cancelled = false;
    setQrLoading(true);

    QRCode.toDataURL(shareUrl, {
      width: 320,
      margin: 1,
      errorCorrectionLevel: 'M',
    })
      .then((url) => {
        if (cancelled) return;
        setQrDataUrl(url);
      })
      .catch(() => {
        if (cancelled) return;
        setQrDataUrl('');
      })
      .finally(() => {
        if (cancelled) return;
        setQrLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [modalOpen, shareUrl]);

  const fallbackCopy = () => {
    const el = inputRef.current;
    if (!el) return false;
    el.focus();
    el.select();
    try {
      return document.execCommand('copy');
    } catch {
      return false;
    }
  };

  const handleCopy = async () => {
    if (!shareUrl) return;

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        const ok = fallbackCopy();
        if (!ok) throw new Error('fallback copy failed');
      }
      onCopySuccess && onCopySuccess();
    } catch {
      onCopyFail && onCopyFail();
    }
  };

  const handleDownloadQr = () => {
    if (!qrDataUrl) return;
    const a = document.createElement('a');
    a.href = qrDataUrl;
    a.download = safeFileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handleOpenChat = () => {
    if (!assistantToken) return;
    window.open(`/chat/${assistantToken}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <Modal
      show={modalOpen}
      centered
      backdrop="static"
      className={styles.modalRoot}
      dialogClassName={styles.dialog}
      contentClassName={styles.modalContent}
      onHide={onHide}
    >
      <Modal.Header className={styles.modalHeader}>
        <Modal.Title className={styles.modalTitle}>
          <i className="bi bi-share" aria-hidden="true" />
          分享對話網址
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className={styles.modalBody}>
        <div className={`row g-3 ${styles.bsRow}`}>
          <div className={`col-12 ${styles.leftCol}`}>
            <div className={styles.assistantLine}>
              <span className={styles.assistantLabel}>代理人：</span>
              <span className={styles.assistantName} title={assistantName || ''}>
                {assistantName || '（未命名）'}
              </span>
            </div>
          </div>
          
          <div className={`col-12 mt-0 ${styles.leftCol}`}>
            <div className={styles.label}>對話網址</div>

            <div className={styles.urlRow}>
              <input
                ref={inputRef}
                type="text"
                className={styles.urlInput}
                value={shareUrl}
                readOnly
                onFocus={(e) => e.target.select()}
              />
              <button
                type="button"
                className={styles.copyBtn}
                onClick={handleCopy}
                disabled={!shareUrl}
              >
                <i className="bi bi-clipboard" aria-hidden="true" />
                複製
              </button>
            </div>
          </div>

          <div className={`col-12 ${styles.rightCol}`}>
            <div className={styles.qrBox}>
              {qrLoading ? (
                <div className={styles.qrLoading}>
                  <span className="spinner-border" role="status" aria-hidden="true" />
                  <div className={styles.qrLoadingText}>產生 QR code...</div>
                </div>
              ) : qrDataUrl ? (
                <img className={styles.qrImage} src={qrDataUrl} alt="QR code" />
              ) : (
                <div className={styles.qrEmpty}>
                  <i className="bi bi-qr-code" aria-hidden="true" />
                  <div>無法產生 QR code</div>
                </div>
              )}
            </div>

            <button
              type="button"
              className={styles.downloadBtn}
              onClick={handleDownloadQr}
              disabled={!qrDataUrl}
            >
              <i className="bi bi-download" aria-hidden="true" />
              下載 QR code
            </button>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer className={styles.footer}>
        <button type="button" className={styles.btnClose} onClick={onHide}>
          關閉
        </button>
      </Modal.Footer>
    </Modal>
  );
}
